import React, { useState, useEffect, memo, useCallback } from 'react';
import { motion } from 'framer-motion';
import type { Style } from '../types';
import type { HairLength, HairVibe } from '../config/hairstyles';
// Scissors icon removed - Apple premium style
import { useLiteModeContext } from '../hooks/useLiteMode';

interface StyleCardProps {
    style: Style;
    isSelected?: boolean;
    onSelect: (styleId: string) => void;
    previewImage?: string;
    showImage?: boolean;
    shortDescription?: string;
    variations?: string;
    length?: HairLength;
    vibe?: HairVibe;
}

// Фиксированные размеры для изображений (предотвращает layout shift)
const IMAGE_WIDTH = 400;
const IMAGE_HEIGHT = 192;

const CATALOG_IMAGE_EXTENSION_OVERRIDES: Record<string, string> = {
    curly_volume: 'webp',
    mullet: 'jpg',
    caesar: 'jpg',
    crop: 'jpg',
    buzz: 'jpg',
};

const getCatalogPreviewCandidates = (styleId: string, previewImage?: string): string[] => {
    const extension = CATALOG_IMAGE_EXTENSION_OVERRIDES[styleId] || 'png';
    const candidates = [
        previewImage,
        `/styles/catalog/${styleId}.${extension}`,
        extension !== 'png' ? `/styles/catalog/${styleId}.png` : undefined,
        extension !== 'jpg' ? `/styles/catalog/${styleId}.jpg` : undefined,
    ].filter(Boolean) as string[];

    return Array.from(new Set(candidates));
};

const StyleCardComponent: React.FC<StyleCardProps> = ({
    style,
    isSelected = false,
    onSelect,
    previewImage,
    showImage = true,
    shortDescription,
}) => {
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const [imageLoaded, setImageLoaded] = useState(false);
    const { isLiteMode, prefersReducedMotion } = useLiteModeContext();
    const imageCandidates = getCatalogPreviewCandidates(style.id, previewImage);
    const activeImageSrc = imageCandidates[activeImageIndex];

    // Мемоизируем обработчик клика
    const handleSelect = useCallback(() => {
        onSelect(style.id);
    }, [onSelect, style.id]);

    const handleImageError = useCallback(() => {
        const failedUrl = imageCandidates[activeImageIndex];
        console.warn('[StyleCard] Preview image failed', {
            styleId: style.id,
            url: failedUrl,
        });
        setImageLoaded(false);
        setActiveImageIndex((index) => index + 1);
    }, [activeImageIndex, imageCandidates, style.id]);

    useEffect(() => {
        setActiveImageIndex(0);
        setImageLoaded(false);
    }, [previewImage, style.id]);

    // Apple-style animations: calm, heavy, controlled
    const shouldAnimate = !isLiteMode && !prefersReducedMotion;
    const hoverAnimation = shouldAnimate ? { scale: 1.005, y: -0.5 } : {};
    const tapAnimation = shouldAnimate ? { scale: 0.97, y: 0.5, transition: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] } } : {};

    // Grid / Compact view without image (text only)
    if (!showImage) {
        return (
            <motion.div
                onClick={handleSelect}
                whileHover={hoverAnimation}
                whileTap={tapAnimation}
                className="relative cursor-pointer rounded-2xl border overflow-hidden flex flex-col p-4 min-h-[130px] justify-between"
                style={{
                    background: 'linear-gradient(145deg, #0B0B0D 0%, #141416 100%)',
                    border: isSelected 
                        ? '1px solid rgba(212,175,55,0.28)' 
                        : '1px solid rgba(255,255,255,0.05)',
                    boxShadow: isSelected
                        ? 'inset 0 1px 0 rgba(212,175,55,0.12), 0 4px 12px rgba(0,0,0,0.5)'
                        : 'inset 0 1px 0 rgba(255,255,255,0.06), 0 4px 12px rgba(0,0,0,0.45)',
                    transition: 'all 320ms cubic-bezier(0.25, 0.1, 0.25, 1)',
                    willChange: 'transform'
                }}
            >
                {/* Subtle glow on selected - no harsh effects */}
                {isSelected && (
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(212,175,55,0.06)_0%,transparent_60%)]" />
                )}

                {/* Apple-style selection indicator - top right only */}
                <div className="absolute top-3 right-3 z-10">
                    <div
                        className="w-6 h-6 rounded-full flex items-center justify-center"
                        style={{
                            background: isSelected 
                                ? 'linear-gradient(145deg, rgba(212,175,55,0.9) 0%, rgba(180,145,40,0.85) 100%)' 
                                : 'rgba(255,255,255,0.08)',
                            border: isSelected ? 'none' : '1.5px solid rgba(255,255,255,0.15)',
                            boxShadow: isSelected 
                                ? '0 2px 8px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.15)'
                                : 'inset 0 1px 0 rgba(255,255,255,0.08)',
                            transform: isSelected ? 'scale(1)' : 'scale(0.92)',
                            opacity: isSelected ? 1 : 0.5,
                            transition: 'all 380ms cubic-bezier(0.25, 0.1, 0.25, 1)'
                        }}
                    >
                        {isSelected && <Icons.Check size={14} className="text-black" strokeWidth={3} />}
                    </div>
                </div>

                <div className="relative z-10 mt-8">
                    <h3 className="text-[15px] font-medium mb-1 leading-tight" style={{ color: isSelected ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.85)' }}>
                        {style.title}
                    </h3>
                    {shortDescription && (
                        <p className="text-[11px] line-clamp-2 leading-relaxed" style={{ color: 'rgba(255,255,255,0.48)' }}>
                            {shortDescription}
                        </p>
                    )}
                </div>
            </motion.div>
        );
    }

    // Default card with image (Main screen recommendations & Catalog)
    return (
        <motion.div
            onClick={handleSelect}
            whileHover={hoverAnimation}
            whileTap={tapAnimation}
            className="relative cursor-pointer rounded-2xl border overflow-hidden"
            style={{
                background: 'linear-gradient(145deg, #0B0B0D 0%, #141416 100%)',
                border: isSelected 
                    ? '1px solid rgba(212,175,55,0.28)' 
                    : '1px solid rgba(255,255,255,0.05)',
                boxShadow: isSelected
                    ? 'inset 0 1px 0 rgba(212,175,55,0.12), 0 6px 18px rgba(0,0,0,0.5)'
                    : 'inset 0 1px 0 rgba(255,255,255,0.06), 0 6px 18px rgba(0,0,0,0.45)',
                transition: 'all 320ms cubic-bezier(0.25, 0.1, 0.25, 1)',
                willChange: 'transform'
            }}
        >
            {/* Top highlight - subtle 3D lip */}
            <div className="absolute inset-x-0 top-0 h-[1.5px] bg-gradient-to-r from-transparent via-white/[0.08] to-transparent z-30" />
            
            {/* Preview Image - 3:4 portrait aspect ratio */}
            <div className="relative w-full aspect-[3/4] overflow-hidden group" style={{ background: 'linear-gradient(145deg, #0a0a0c 0%, #060607 100%)' }}>
                {activeImageSrc ? (
                    <>
                        <img
                            src={activeImageSrc}
                            alt={style.title}
                            width={IMAGE_WIDTH}
                            height={IMAGE_HEIGHT}
                            loading="lazy"
                            decoding="async"
                            className={`w-full h-full object-cover object-center transition-all duration-500 ${
                                imageLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
                            } ${isSelected ? 'brightness-100' : 'brightness-90 group-hover:brightness-100'}`}
                            onLoad={() => setImageLoaded(true)}
                            onError={handleImageError}
                        />
                        {!imageLoaded && (
                            <div className="absolute inset-0 flex items-center justify-center bg-[#0F0F0F]">
                                <div className="w-6 h-6 border-2 border-white/10 border-t-white/40 rounded-full animate-spin" />
                            </div>
                        )}
                    </>
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ background: 'linear-gradient(145deg, #0a0a0c 0%, #060607 100%)' }}>
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>{style.title}</span>
                    </div>
                )}
                
                {/* Gradient overlay - calm and subtle */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent opacity-70" />

                {/* Selection Indicator - Apple style */}
                <div className="absolute top-3 right-3 z-20">
                    <div
                        className="w-6 h-6 rounded-full flex items-center justify-center"
                        style={{
                            background: isSelected 
                                ? 'linear-gradient(145deg, rgba(212,175,55,0.95) 0%, rgba(180,145,40,0.9) 100%)' 
                                : 'rgba(0,0,0,0.4)',
                            backdropFilter: 'blur(4px)',
                            WebkitBackdropFilter: 'blur(4px)',
                            border: isSelected ? 'none' : '1.5px solid rgba(255,255,255,0.25)',
                            boxShadow: isSelected 
                                ? '0 2px 10px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.2)'
                                : '0 2px 6px rgba(0,0,0,0.3)',
                            transform: isSelected ? 'scale(1)' : 'scale(0.95)',
                            transition: 'all 380ms cubic-bezier(0.25, 0.1, 0.25, 1)'
                        }}
                    >
                        {isSelected && <Icons.Check size={14} className="text-black" strokeWidth={3} />}
                    </div>
                </div>

                {/* Content Overlay - calm typography */}
                <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
                    <h3 className="text-[15px] font-medium mb-0.5 leading-tight" style={{ color: 'rgba(255,255,255,0.95)' }}>
                        {style.title}
                    </h3>
                    {shortDescription && (
                        <p className="text-[11px] line-clamp-2 leading-[1.35]" style={{ color: 'rgba(255,255,255,0.65)' }}>
                            {shortDescription}
                        </p>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

const Icons = {
    Check: ({ size = 24, className = "", strokeWidth = 2 }: { size?: number, className?: string, strokeWidth?: number }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
            <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
    )
};

// Мемоизируем компонент для предотвращения лишних ре-рендеров
export const StyleCard = memo(StyleCardComponent);

